import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  computed,
  input,
} from '@angular/core';
import type {
  Mode,
  RecState,
  Stage,
  StageStep,
  StageStepState,
} from '../../state/recorder.types';

/**
 * StageStepperComponent — non-interactive workflow pill.
 *
 * Recording mode (default):
 *   Setup → Recording → Playback / Review
 *
 * Practice mode:
 *   Setup → Practicing
 *
 * Practice mode never shows Playback / Review.
 *
 * States per step: active | passed | reachable | locked. The Recording step
 * uses a pink + glow style when active; the Practicing step uses the same
 * pink + glow treatment so the rehearsal flow reads as "live". When
 * `setupComplete` is false, the Recording step shows a tooltip prompting the
 * user to complete Setup.
 */
@Component({
  selector: 'rec-stage-stepper',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './stage-stepper.component.html',
  styleUrls: ['./stage-stepper.component.scss'],
})
export class StageStepperComponent {
  /** Convenience input — pass either the rec state or the current stage. */
  readonly recState = input<RecState | null>(null);
  readonly current = input<Stage>('setup');
  readonly setupComplete = input<boolean>(false);
  readonly compact = input<boolean>(false);
  /** Active workflow. Practice swaps the rendered step list. */
  readonly mode = input<Mode>('recording');

  private readonly RECORDING_STEPS: ReadonlyArray<StageStep> = [
    { id: 'setup', label: 'Setup' },
    { id: 'record', label: 'Recording' },
    { id: 'review', label: 'Playback' },
  ];

  private readonly PRACTICE_STEPS: ReadonlyArray<StageStep> = [
    { id: 'setup', label: 'Setup' },
    { id: 'practice', label: 'Practicing' },
  ];

  readonly steps = computed<ReadonlyArray<StageStep>>(() =>
    this.mode() === 'practice' ? this.PRACTICE_STEPS : this.RECORDING_STEPS
  );

  /** Stage resolved from `recState` when provided, else `current`. */
  readonly stage = computed<Stage>(() => {
    const rs = this.recState();
    if (rs === 'recording') return 'record';
    if (rs === 'review') return 'review';
    if (rs === 'practicing') return 'practice';
    if (rs === 'idle' || rs === 'practice-stopped') return 'setup';
    return this.current();
  });

  readonly currentIndex = computed(() =>
    this.steps().findIndex((s) => s.id === this.stage())
  );

  @HostBinding('attr.data-compact')
  get compactAttr() {
    return this.compact() ? 'true' : null;
  }

  @HostBinding('attr.data-mode')
  get modeAttr() {
    return this.mode();
  }

  stateOf(index: number): StageStepState {
    const cur = this.currentIndex();
    const step = this.steps()[index];
    if (index === cur) return 'active';
    if (cur >= 0 && index < cur) return 'passed';
    // For unvisited steps after the current one: Recording / Practicing are
    // locked until setup is complete; everything else is reachable.
    if ((step.id === 'record' || step.id === 'practice') && !this.setupComplete()) {
      return 'locked';
    }
    return 'reachable';
  }

  /** True for the Recording (or Practicing) step when setup is not yet complete. */
  showSetupTooltip(index: number): boolean {
    const id = this.steps()[index].id;
    return (id === 'record' || id === 'practice') && !this.setupComplete();
  }

  trackById = (_: number, s: StageStep) => s.id;
}
